import { ShieldCheck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { formatApiError } from "../api/client";
import { Field } from "../components/PageHeader";

export function LoginPage() {
  const { session, loading, loginStaff, loginCitizen } = useAuth();
  const [mode, setMode] = useState<"staff" | "citizen">("staff");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  if (!loading && session)
    return <Navigate to={session.subjectType === "citizen" ? "/citizen" : "/"} replace />;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const data = new FormData(event.currentTarget);
    const value = (name: string) => {
      const item = data.get(name);
      return typeof item === "string" ? item : "";
    };
    try {
      if (mode === "staff") await loginStaff(value("username"), value("password"));
      else await loginCitizen(value("phone"), value("pin"));
      const requested = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
      void navigate(requested ?? (mode === "staff" ? "/" : "/citizen"), { replace: true });
    } catch (reason) {
      setError(formatApiError(reason));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-intro" aria-labelledby="welcome-title">
        <div className="brand brand--large">
          <span className="brand-mark">ت</span>
          <span>
            <strong>تموين</strong>
            <small>منصة إدارة توزيع أسطوانات الغاز</small>
          </span>
        </div>
        <div>
          <p className="eyebrow">خدمة المجتمع تبدأ ببيانات سليمة</p>
          <h1 id="welcome-title">توزيع عادل، موثّق، ويمكن تتبعه</h1>
          <p>نظام متكامل لإدارة الأسر والوكلاء والدفعات والتسليم وفق أدوار ونطاقات واضحة.</p>
        </div>
        <div className="trust-note">
          <ShieldCheck aria-hidden="true" />
          <span>اتصال آمن وجلسة محمية. لا تشارك كلمة المرور أو رمز الدخول.</span>
        </div>
      </section>
      <section className="login-panel" aria-labelledby="login-title">
        <div className="segmented" role="tablist" aria-label="نوع الدخول">
          <button
            role="tab"
            aria-selected={mode === "staff"}
            onClick={() => {
              setMode("staff");
              setError(null);
            }}
          >
            دخول الموظفين
          </button>
          <button
            role="tab"
            aria-selected={mode === "citizen"}
            onClick={() => {
              setMode("citizen");
              setError(null);
            }}
          >
            بوابة المواطن
          </button>
        </div>
        <h2 id="login-title">{mode === "staff" ? "مرحبًا بعودتك" : "استعلم عن دور أسرتك"}</h2>
        <p>
          {mode === "staff" ? "استخدم حساب العمل المعتمد." : "أدخل رقم الهاتف ورمز الدخول الخاص بالأسرة."}
        </p>
        <form className="form-stack" onSubmit={submit}>
          {mode === "staff" ? (
            <>
              <Field label="اسم المستخدم" htmlFor="username">
                <input id="username" name="username" autoComplete="username" required />
              </Field>
              <Field label="كلمة المرور" htmlFor="password">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  minLength={8}
                />
              </Field>
            </>
          ) : (
            <>
              <Field label="رقم الهاتف" htmlFor="phone">
                <input id="phone" name="phone" inputMode="tel" autoComplete="tel" required />
              </Field>
              <Field label="رمز الدخول" htmlFor="pin">
                <input
                  id="pin"
                  name="pin"
                  type="password"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  minLength={4}
                />
              </Field>
            </>
          )}
          {error ? (
            <div className="inline-alert" role="alert">
              {error}
            </div>
          ) : null}
          <button className="button button--wide" type="submit" disabled={submitting}>
            {submitting ? "جارٍ التحقق…" : "دخول آمن"}
          </button>
        </form>
      </section>
    </main>
  );
}
